import React from "react";

import { serializeError } from "serialize-error";

import tiktokLogo from "@assets/tiktok-logo.svg";
import { useUser } from "@components/user-context";
import PartnerConsentSwitch from "@features/partner-consent/shared/partner-consent-switch";
import {
  ConnectionType,
  Partner,
  PartnerPermissionEnum,
} from "@north-beam/nb-client-side";
import {
  ConnectionCard,
  type PartnerConsentComponentProps,
} from "@pages/settings/connection-card";
import {
  ConnectionStatusType,
  type LoginComponentChildrenProps,
  type LoginComponentWithFrontendIntegrationProps,
} from "@pages/settings/connection-status-manager";
import { type CardProps2 } from "@pages/settings/core";
import { PrimaryButton } from "@shared/buttons";
import { extractError } from "@utils/index";
import { launchOAuthPopup } from "@utils/oauth2-handler";

import { type AccountMetadataWithDescription } from "../account-selector";

const PartnerConsent = ({
  hideWhenMissingConsent,
}: PartnerConsentComponentProps) => (
  <PartnerConsentSwitch
    partner={Partner.TIKTOK}
    permissions={[PartnerPermissionEnum.data_impression_ingestion]}
    label="Turn on to use Clicks + Deterministic Views Model"
    hideWhenMissingConsent={hideWhenMissingConsent}
  />
);

export function TikTokConnectionCard(props: CardProps2) {
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="TikTok Ads"
      connectionCallToAction={`
        Integrate your TikTok Ads data to start receiving insights about
        your TikTok ad spend.
      `}
      logoUrl={tiktokLogo}
      logoAltText="TikTok Ads Logo"
      partnerConsentComponent={PartnerConsent}
      knowledgeBaseLink="https://docs.northbeam.io/docs/setting-up-tiktok-ads-for-northbeam"
    >
      {({ setStatus, accounts }: LoginComponentChildrenProps) => (
        <TikTokLoginComponent
          setStatus={setStatus}
          accounts={accounts}
          integrationDetails={props.integrationDetails}
        />
      )}
    </ConnectionCard>
  );
}

function TikTokLoginComponent({
  setStatus,
  integrationDetails,
}: LoginComponentWithFrontendIntegrationProps) {
  const { user } = useUser();
  const [isButtonDisabled, setButtonDisabled] = React.useState(false);

  const { tiktokAuthBlob } = integrationDetails;

  const onConnectClicked = React.useCallback(async () => {
    try {
      setButtonDisabled(true);

      const tokenPayload = await openTikTokPopup(tiktokAuthBlob.client_id);

      const result = await user.callApi<{
        error: string;
        accounts: AccountMetadataWithDescription[];
      }>("/api/callbacks/tiktok", {
        method: "post",
        data: {
          tokenPayload,
          redirectURI: `${window.location.origin}/callbacks/tiktok`,
        },
      });

      if (result.error) {
        setStatus({
          type: ConnectionStatusType.HAS_ERROR,
          errorMessage: `An error occurred: ${serializeError(
            extractError(result.error),
          )}`,
        });
      } else {
        setStatus({
          type: ConnectionStatusType.SELECTING_ACCOUNT,
          accountList: result.accounts,
        });
      }
    } catch (e) {
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage: `An error occurred: ${serializeError(extractError(e))}`,
      });
    }

    setButtonDisabled(false);
  }, [setStatus, setButtonDisabled, user, tiktokAuthBlob]);

  return (
    <div className="mb-3">
      <div className="row">
        <div className="col-auto">
          <PrimaryButton disabled={isButtonDisabled} onClick={onConnectClicked}>
            {isButtonDisabled ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

async function openTikTokPopup(oauthClientId: string) {
  const params = new URLSearchParams();

  params.append("app_id", oauthClientId);
  params.append("redirect_uri", `${window.location.origin}/callbacks/tiktok`);

  return launchOAuthPopup(
    `https://ads.tiktok.com/marketing_api/auth/?${params.toString()}`,
  );
}

const type = ConnectionType.integration_tiktok;
